<script setup lang="ts">
import type { ConfiguratorPool } from '~/models/configurator/configuratorPool.model'
import type { ConfiguratorPoolLine } from '~/models/configurator/configuratorPoolLine.model'
import type { DirectusImage } from '~/models/directus/directusImage.model'
import { useKeenSlider } from 'keen-slider/vue'
import { fallTransition } from '~/transitions/fall.transition'
import AppDirectusImage from '../app/AppDirectusImage.vue'
import AppButton from '../app/button/AppButton.vue'
import 'keen-slider/keen-slider.min.css'

const props = defineProps<{
  poolLines: ConfiguratorPoolLine[]
  currentPool: ConfiguratorPool | null
  backgroundImageDay: DirectusImage
  backgroundImageNight: DirectusImage
  formLabel: string
}>()

const emits = defineEmits(['openForm'])

const currentPoolLine = defineModel<ConfiguratorPoolLine>('poolLine', {
  required: true,
})

const { dt } = useDirectusTranslation()

const isNight = ref(false)
const hasDeck = ref(false)
const isEmpty = ref(false)

const poolImageShown = computed<DirectusImage | null>(() => {
  if (!props.currentPool)
    return null

  if (isNight.value)
    return props.currentPool?.poolImageNight

  if (isEmpty.value)
    return props.currentPool?.poolImageEmpty

  return props.currentPool?.poolImage
})

const [container, slider] = useKeenSlider({
  loop: true,
  renderMode: 'performance',
  initial: getCurrentPoolLineIndex(),
  drag: true,
  slideChanged: (slide) => {
    setCurrentPoolLine(slide.track.details.rel)
  },
  slides: {
    perView: 1.2,
    origin: 'center',
  },
  breakpoints: {
    '(min-width: 1024px)': {
      slides: {
        perView: 1.2,
        origin: 'center',

      },
    },
    '(min-width: 1280px)': {
      slides: {
        perView: 1.2,
        origin: 'center',

      },
    },
    '(min-width: 1680px)': {
      slides: {
        perView: 1.5,
        origin: 'center',

      },
    },

  },

})

const poolLinesAmount = computed<number>(() => {
  return props.poolLines.length
})

function setCurrentPoolLine(slideNumber: number) {
  currentPoolLine.value = props.poolLines[slideNumber % poolLinesAmount.value]
}

function slideToPoolLine(poolLine: ConfiguratorPoolLine) {
  const index = props.poolLines.indexOf(poolLine)
  slider.value?.moveToIdx(index)
}

function getCurrentPoolLineIndex() {
  return props.poolLines.indexOf(currentPoolLine.value)
}

const backgroundImage = computed<DirectusImage>(() => {
  return isNight.value ? props.backgroundImageNight : props.backgroundImageDay
})

function isActiveSlide(index: number): boolean {
  return index === slider.value?.track.details.rel
}

setCurrentPoolLine(getCurrentPoolLineIndex())

defineExpose({
  slideToPoolLine,
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <ConfiguratorPoolLineSelector
      :pool-lines="poolLines"
      :current-pool-line="currentPool?.poolLine ?? null"
      @select-pool-line="slideToPoolLine"
    />
    <div ref="container" class="keen-slider pointer-events-none lg:pointer-events-auto">
      <template v-for="(item, index) in props.poolLines" :key="item.id">
        <div class="keen-slider__slide relative flex items-center justify-center text-3xl">
          <ConfiguratorControls
            v-if="index === getCurrentPoolLineIndex()"
            v-model:is-night="isNight"
            v-model:is-empty="isEmpty"
            v-model:has-deck="hasDeck"
            class="pointer-events-auto absolute left-4 top-4 z-20"
            :can-deck="!!currentPool?.poolImageDeck.id"
            :can-night="!!currentPool?.poolImageNight.id"
            :can-empty="!!currentPool?.poolImageEmpty.id"
          />
          <div
            class="h-full w-full flex items-center justify-center overflow-hidden transition" :class="{
              '-rotate-y-10 scale-90': !isActiveSlide(index),
            }"
          >
            <Transition
              v-bind="fallTransition"
            >
              <AppDirectusImage
                v-if="poolImageShown" :key="poolImageShown.id ?? 'poolKey'" class="absolute z-10 h-full w-full scale-150 object-[50%_45%] lg:scale-100" :width="1200"
                :image="poolImageShown"
              />
            </Transition>
            <Transition
              v-bind="fallTransition"
            >
              <AppDirectusImage
                v-if="currentPool?.poolImageDeck && hasDeck"
                :key="currentPool?.poolImageDeck.id ?? 'poolKey'" :width="1200"
                class="absolute z-10 h-full w-full scale-150 object-[50%_45%] lg:scale-100" :image="currentPool?.poolImageDeck"
              />
            </Transition>

            <Transition :duration="200" name="fade" mode="out-in">
              <KeepAlive>
                <AppDirectusImage
                  :key="backgroundImage.id"
                  class="h-full w-full scale-150 object-[50%_45%] lg:scale-100"
                  :width="1200"
                  :image="backgroundImage"
                />
              </KeepAlive>
            </Transition>
            <div class="absolute bottom-4 right-4 z-50 hidden flex-col items-center gap-4 bg-black/60 p-8 text-center backdrop-blur-[6px] lg:flex">
              <p class="text-2xl font-semibold">
                {{ dt(item?.translations)?.name }}
              </p>
              <div class="h-px w-8 bg-white" />

              <div class="w-full flex flex-col gap-3 text-xl">
                <p v-for="usp in dt(item?.translations)?.usps" :key="usp">
                  {{ usp }}
                </p>
              </div>
              <div class="grid grid-cols-1 w-full gap-4">
                <AppButton variant="secondary" size="full" @click="emits('openForm')">
                  {{ props.formLabel }}
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
